import { autobind } from 'core-decorators'
import { inject, observer } from 'mobx-react'
import * as React from 'react'
import { Button, ButtonBar, NextButton } from '../../../shared/components/Button'
import { CheckboxControl } from '../../../shared/components/CheckboxControl'
import { ContentContainerBody, ContentRow, FlexItem, SplitContentContainer } from '../../../shared/components/Content'
import { CheckboxLabel, Headlines, InnerContainer, SimpleLink, Text } from '../../../shared/components/Elements'
import { Headline, SubHeadline } from '../../../shared/components/Headlines'
import { Loading } from '../../../shared/components/Loading'
import { SideInformation } from '../../../shared/components/SideInformation'
import { TextControl } from '../../../shared/components/TextControl'
import { Jaw } from '../../../shared/Constants'
import { breakpoint, styled } from '../../../shared/styled-components'
import { CalculatorStoreInstance } from '../../../stores/CalculatorStore'
import { SegmentedProgressBar } from './result/SegmentedProgressBar'

interface IProps {
  store?: CalculatorStoreInstance
  history?: History
  jaw: Jaw
}

interface IState {
  privacyPolicyChecked: boolean
  loading: boolean
}

@inject('store')
@observer
export class PersonalDataView extends React.Component<IProps, IState> {
  state: IState = {
    privacyPolicyChecked: false,
    loading: false
  }

  // -------------------------------------------------------------------------- LIFECYCLE

  componentDidMount () {
    const { store } = this.props
    store!.validateRequirementsGiven()
  }

  // -------------------------------------------------------------------------- EVENTS

  @autobind
  private async _onNextClick () {
    const { store } = this.props
    const { zipCode, email } = store!.offer
    if (!zipCode) {
      return alert('Bitte geben Sie eine PLZ oder eine Stadt an.')
    }
    if (!email || !(/(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/).test(email)) {
      return alert('Bitte geben Sie eine gültige E-Mail-Adresse an')
    }
    // @ts-ignore
    if (parseInt(zipCode, 10) == zipCode && zipCode.length < 5) {
      return alert('Bitte geben Sie Ihre vollständige PLZ an.')
    }

    if (!this.state.privacyPolicyChecked) {
      return alert('Sie müssen die Datenschutzbestimmungen gelesen und akzeptiert haben, um fortzufahren.')
    }

    store!.setPersonalDataDone(true)
    store!.goForward()
  }

  private _onPropChange (prop: string, value: string) {
    this.props.store!.offer.setProp(prop, value)
  }

  // -------------------------------------------------------------------------- RENDERING

  render () {
    const { store } = this.props
    return (<InnerContainer padded>
      {this.state.loading ? <Loading /> : <>
        <Headlines>
          <Headline centered>Fast geschafft!</Headline>
          <SubHeadline centered>Ihre Postleitzahl wird für die Lokalisierung Ihres individuellen implacheck®-Experten benötigt. </SubHeadline>
        </Headlines>

        <SplitContentContainer>
          <ContentContainerBody vertical>
            <TopContentRow>
              <TextContainer>
                <InfoText>Ihre Daten werden vertraulich behandelt und nicht an Dritte weitergegeben. Bitte geben Sie Ihre vollständige PLZ an. Um Ihren persönlichen Bericht als PDF per E-Mail zu erhalten, geben Sie bitte Ihre E-Mail-Adresse an. In diesem Zuge erhalten Sie über unseren implacheck®-Newsletter auch relevante und wertvolle Informationen rund um das Thema Implantatversorgung. </InfoText>
                <RequiredFieldNotice>* Pflichtfeld</RequiredFieldNotice>
              </TextContainer>
              <FormContainer>
                <TextControl type='text' required label='PLZ*' value={store!.offer.zipCode} onChange={this._onPropChange.bind(this, 'zipCode')} />
                <TextControl type='email' label='E-Mail*' required value={store!.offer.email} onChange={this._onPropChange.bind(this, 'email')} />
                <CheckboxControl checked={this.state.privacyPolicyChecked} onChange={() => { this.setState({ privacyPolicyChecked: !this.state.privacyPolicyChecked })}}>
                  <CheckboxLabel>Ich stimme den <SimpleLink href='https://www.implacheck.de/Datenschutz' target='_blank' onClick={e => e.stopPropagation()}>Datenschutzbestimmungen</SimpleLink> zu *</CheckboxLabel>
                </CheckboxControl>
              </FormContainer>
            </TopContentRow>
          </ContentContainerBody>
          <SideInformation
            onBackClick={() => this.props.store!.goBack()}
            onNextClick={this._onNextClick}
            infoText='Sie bundesweit die Möglichkeit haben, sich von einem implacheck®-Experten behandeln zu lassen?'
            longInfoText='Sie bundesweit die Möglichkeit haben sich von einem implacheck®-Experten behandeln zu lassen? Unser weitläufiges Expertennetzwerk sorgt dafür, dass wir bundesweit eine ganzheitliche Versorgung unserer Patienten realisieren können. Sobald Sie die ersten drei Ziffern Ihrer Postleitzahl angeben, schlagen wir Ihnen drei implacheck®-Netzwerkpartner vor, die Sie sich auf den jeweiligen Unterseiten gesondert ansehen können. Auf Basis Ihrer Eindrücke können Sie Ihren favorisierten Behandler wählen und sich unverbindlich mit der Partner-Praxis in Verbindung setzen. '
            />
        </SplitContentContainer>

        <ButtonBar>
          <Button onClick={() => this.props.store!.goBack()}>Zurück</Button>
          <SegmentedProgressBar segmentCount={2} segment={1} />
          <NextButton primary onClick={this._onNextClick}>Ergebnis anzeigen</NextButton>
        </ButtonBar>
      </InnerContainer>
    </>)
  }
}

const InfoText = styled(Text)`
  max-width: 360px;
`

const TextContainer = styled(FlexItem)`
  display: flex;
  flex-direction: column;
  padding-bottom: 20px;
  ${breakpoint('tablet')`
    justify-content: space-between;
  `}
`

const TopContentRow = styled(ContentRow)`
  ${breakpoint('tablet')`
    padding-top: 5em;
  `}
`

const FormContainer = styled(FlexItem)`
  margin-bottom: 20px;
  ${breakpoint('tablet')`
    margin-bottom: 0;
  `}
`

const RequiredFieldNotice = styled(InfoText)`
  margin-top: 20px;
  ${breakpoint('tablet')`
    margin-top: 0;
  `}
`
